// Upgrading @atlaskit/platform; fix these violations.
// eslint-disable-next-line @compiled/jsx-pragma
/** @jsxImportSource @compiled/react */
import React from 'react';
import { css } from '@compiled/react';
import type {
	Extension,
	ExtensionData,
	ForgePageName,
	ForgeRunnerProps,
	ForgeRunnerPropsWithExtension,
	RunnerExtensionData,
	WithBlockedAppsBannerProps,
} from '@helpcenter/forge-ui-utils';
import { FORGE_CSM_HELP_CENTER, FORGE_HELP_CENTER } from '@helpcenter/forge-ui-utils/src/constants';
import { ExtensionPointProvider } from '@helpcenter/forge-ui-utils/src/extension-point-provider';
import { ExtensionProvider } from '@helpcenter/forge-ui-utils/src/extension-provider';
import { Renderer } from '@helpcenter/forge-ui-utils/src/renderer';
import { useShowBanner } from 'utils/blocked-apps-banner';
import { withExtensionList } from 'view/common/forge-ui/with-extension-list';
import { StyledForgeContainer } from 'view/forge-ui/forge-runner/styled';
import { Headline } from 'view/forge-ui/request-details-main-section';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import { useIsCSMTypographyMigrationEnabled } from '@atlassian/help-center-common-util/enable-typography-migration';

export const ForgeRunnerReactFragment: React.FC<ForgeRunnerPropsWithExtension> = ({
	extension,
	type,
	page,
	portalId,
	requestKey,
	requestTypeId,
	noWrapper,
}) => {
	const pages = extension.properties.pages || [];
	const isCSMTypographyMigrationEnabled = useIsCSMTypographyMigrationEnabled();

	// BEARS-3054: CSM renders only extensions explicitly declaring csm_help_center; other pages keep the all-pages fallback.
	const isEligible =
		page === FORGE_CSM_HELP_CENTER
			? pages.includes(FORGE_CSM_HELP_CENTER)
			: pages.includes(page) || pages.length === 0;

	const extensionData: ExtensionData = React.useMemo(() => {
		const computedExtensionData: RunnerExtensionData = {
			page,
			type,
		};

		if (!!portalId) {
			computedExtensionData.portal = { id: portalId };
		}

		if (requestKey || requestTypeId) {
			computedExtensionData.request = {
				...(requestKey && { key: requestKey }),
				...(requestTypeId && { typeId: requestTypeId }),
			};
		}

		return computedExtensionData;
	}, [page, portalId, requestKey, requestTypeId, type]);

	return isEligible ? (
		// to-do: reconcile with jira customer-service-support-website-edit SharedForgeRunner into one shared runner. See BEARS-3008.
		<React.Fragment key={extension.id}>
			{isCSMTypographyMigrationEnabled ? (
				<div css={headingContainerCSS}>
					<Heading size="xsmall">{extension.properties.title}</Heading>
				</div>
			) : (
				<Headline>{extension.properties.title}</Headline>
			)}
			<ExtensionProvider extension={extension} noWrapper={noWrapper}>
				<Renderer extension={extension} extensionData={extensionData} />
			</ExtensionProvider>
		</React.Fragment>
	) : null;
};

interface WrapperProps {
	page: ForgePageName;
	noWrapper?: boolean;
	skipHelpCenterWrapper?: boolean;
}

export const WithWrapper: React.FC<React.PropsWithChildren<WrapperProps>> = ({
	page,
	children,
	noWrapper,
	skipHelpCenterWrapper,
}) => {
	if (skipHelpCenterWrapper) {
		return <>{children}</>;
	}
	if (page === FORGE_HELP_CENTER) {
		return <StyledForgeContainer noWrapper={!!noWrapper}>{children}</StyledForgeContainer>;
	}
	return <>{children}</>;
};

export const ForgeRunnerModuleDumb: React.FC<ForgeRunnerProps & WithBlockedAppsBannerProps> = ({
	page,
	portalId,
	projectId,
	requestKey,
	requestTypeId,
	type,
	extensions,
	showBanner,
	resetBannerState,
	noWrapper,
	skipHelpCenterWrapper,
}) => {
	const allowedExtensions: Extension[] = useShowBanner(
		extensions,
		page,
		true,
		showBanner,
		resetBannerState,
	);

	return (
		<>
			{allowedExtensions.length > 0 && (
				<ExtensionPointProvider extensionType={type} noWrapper={noWrapper}>
					{allowedExtensions.map(
						(extension: Extension) =>
							extension && (
								<WithWrapper
									page={page}
									key={`${extension.id}-wrapper`}
									noWrapper={!!noWrapper}
									skipHelpCenterWrapper={skipHelpCenterWrapper}
								>
									<ForgeRunnerReactFragment
										key={extension.id}
										type={type}
										page={page}
										portalId={portalId}
										projectId={projectId}
										requestKey={requestKey}
										requestTypeId={requestTypeId}
										extension={extension}
										noWrapper={noWrapper}
									/>
								</WithWrapper>
							),
					)}
				</ExtensionPointProvider>
			)}
		</>
	);
};

export const ForgeRunnerModule = withExtensionList()(ForgeRunnerModuleDumb);

export default ForgeRunnerModule;

const headingContainerCSS = css({
	marginTop: token('space.300', '24px'),
	marginBottom: token('space.100', '8px'),
});
